import { z } from "zod";
import { isValidPhoneNumber } from "libphonenumber-js";
// CLEANUP: Shared constants for consistent rules across the Monorepo
import { VALIDATION_RULES } from "@monorepo/config";
// REFACTOR: Use shared patterns to remove duplication and ensure consistency
import { PASSWORD_PATTERNS } from "../shared/password-strength";

// UI-specific error messages
// These use the shared constants to generate dynamic messages
export const RECOVERY_ERROR_MESSAGES = {
  REQUIRED_FIELD: "This field is required",
  INVALID_EMAIL: "Please enter a valid email address",
  INVALID_PHONE: "Please enter a valid phone number",
  PASSWORD_LENGTH: `Password must be at least ${VALIDATION_RULES.PASSWORD_MIN_LENGTH} characters`,
  PASSWORD_MAX: `Password must not exceed ${VALIDATION_RULES.PASSWORD_MAX_LENGTH} characters`,
  PASSWORD_UPPERCASE: "Password must contain at least one uppercase letter",
  PASSWORD_LOWERCASE: "Password must contain at least one lowercase letter",
  PASSWORD_NUMBER: "Password must contain at least one number",
  PASSWORD_SPECIAL: "Password must contain at least one special character",
  PASSWORD_MISMATCH: "Passwords do not match",
  REASON_LENGTH: `Please provide at least ${VALIDATION_RULES.LOST_ACCESS_REASON_MIN_LENGTH} characters explaining your situation`,
  REASON_MAX: `Reason must not exceed ${VALIDATION_RULES.LOST_ACCESS_REASON_MAX_LENGTH} characters`,
  INVALID_TOKEN: "Invalid token format",
} as const;

/**
 * Schema for password reset request (email entry)
 */
export const passwordResetRequestSchema = z.object({
  email: z
    .string()
    .trim()
    .toLowerCase()
    .min(1, RECOVERY_ERROR_MESSAGES.REQUIRED_FIELD)
    .email(RECOVERY_ERROR_MESSAGES.INVALID_EMAIL),
});

export type PasswordResetRequestInput = z.infer<
  typeof passwordResetRequestSchema
>;

/**
 * Schema for password reset confirmation
 * SECURITY: Enforces policies defined in packages/config
 * REFACTOR: Uses PASSWORD_PATTERNS from password-strength.ts
 */
export const passwordResetConfirmSchema = z
  .object({
    password: z
      .string()
      .min(VALIDATION_RULES.PASSWORD_MIN_LENGTH, RECOVERY_ERROR_MESSAGES.PASSWORD_LENGTH)
      .max(VALIDATION_RULES.PASSWORD_MAX_LENGTH, RECOVERY_ERROR_MESSAGES.PASSWORD_MAX)
      .refine((val) => PASSWORD_PATTERNS.UPPER.test(val), {
        message: RECOVERY_ERROR_MESSAGES.PASSWORD_UPPERCASE,
      })
      .refine((val) => PASSWORD_PATTERNS.LOWER.test(val), {
        message: RECOVERY_ERROR_MESSAGES.PASSWORD_LOWERCASE,
      })
      .refine((val) => PASSWORD_PATTERNS.NUMBER.test(val), {
        message: RECOVERY_ERROR_MESSAGES.PASSWORD_NUMBER,
      })
      .refine((val) => PASSWORD_PATTERNS.SPECIAL.test(val), {
        message: RECOVERY_ERROR_MESSAGES.PASSWORD_SPECIAL,
      }),
    confirmPassword: z.string().min(1, RECOVERY_ERROR_MESSAGES.REQUIRED_FIELD),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: RECOVERY_ERROR_MESSAGES.PASSWORD_MISMATCH,
    path: ["confirmPassword"],
  });

export type PasswordResetConfirmInput = z.infer<
  typeof passwordResetConfirmSchema
>;

/**
 * Schema for lost access recovery request
 */
export const lostAccessRequestSchema = z.object({
  email: z
    .string()
    .trim()
    .toLowerCase()
    .min(1, RECOVERY_ERROR_MESSAGES.REQUIRED_FIELD)
    .email(RECOVERY_ERROR_MESSAGES.INVALID_EMAIL),
  phone: z
    .string()
    .optional()
    .refine((val) => {
      if (!val) return true; // Optional field
      // SECURITY: Libphonenumber validation
      return isValidPhoneNumber(val);
    }, RECOVERY_ERROR_MESSAGES.INVALID_PHONE),
  reason: z
    .string()
    .min(
      VALIDATION_RULES.LOST_ACCESS_REASON_MIN_LENGTH,
      RECOVERY_ERROR_MESSAGES.REASON_LENGTH
    )
    .max(
      VALIDATION_RULES.LOST_ACCESS_REASON_MAX_LENGTH,
      RECOVERY_ERROR_MESSAGES.REASON_MAX
    )
    .trim(),
});

export type LostAccessRequestInput = z.infer<typeof lostAccessRequestSchema>;

/**
 * Schema for validating recovery/reset tokens
 */
export const recoveryTokenSchema = z.object({
  token: z
    .string()
    .min(1, RECOVERY_ERROR_MESSAGES.REQUIRED_FIELD)
    .regex(/^[A-Za-z0-9_-]+$/, RECOVERY_ERROR_MESSAGES.INVALID_TOKEN),
});

export type RecoveryTokenInput = z.infer<typeof recoveryTokenSchema>;

// ... (State schemas)
export const passwordResetStateSchema = z.object({
  token: z.string().min(1),
  email: z.string().email().optional(),
});
export type PasswordResetState = z.infer<typeof passwordResetStateSchema>;

export const recoveryStatusStateSchema = z.object({
  requestId: z.string().min(1),
  email: z.string().email().optional(),
});
export type RecoveryStatusState = z.infer<typeof recoveryStatusStateSchema>;

export const recoveryCompleteStateSchema = z.object({
  token: z.string().min(1),
  email: z.string().email().optional(),
});
export type RecoveryCompleteState = z.infer<typeof recoveryCompleteStateSchema>;

export const passwordResetSuccessStateSchema = z.object({
  email: z.string().email().optional(),
  message: z.string().optional(),
});
export type PasswordResetSuccessState = z.infer<
  typeof passwordResetSuccessStateSchema
>;

// ============================================================================
// Advanced Account Recovery Schemas (New 6-Step Protocol)
// ============================================================================

/**
 * Screen 1: Identify Account
 */
export const identifyAccountSchema = z.object({
  identifier: z
    .string()
    .min(1, "Email or Username is required")
    .trim(),
  // captchaToken is handled separately via state, not form input
});
export type IdentifyAccountInput = z.infer<typeof identifyAccountSchema>;

/**
 * Screen 4: Verify Authority (OTP/Backup Code)
 */
export const verifyAuthoritySchema = z.object({
  code: z
    .string()
    .min(1, RECOVERY_ERROR_MESSAGES.REQUIRED_FIELD)
    .trim()
    .max(24, "Invalid code length"), // OTP is 6, Backup varies (e.g. 8-16)
  // Note: We don't regex strictly to allow formats like "123 456" (stripped later)
});
export type VerifyAuthorityInput = z.infer<typeof verifyAuthoritySchema>;

/**
 * Screen 5: Reset Password (Complete)
 * Same as legacy confirm schema but with explicit naming
 */
export const resetPasswordCompleteSchema = passwordResetConfirmSchema;
export type ResetPasswordCompleteInput = z.infer<typeof resetPasswordCompleteSchema>;

// --- Routing/State Schemas ---

export const recoveryMethodStateSchema = z.object({
  preAuthToken: z.string().min(1),
  sessionId: z.string().min(1),
});
export type RecoveryMethodState = z.infer<typeof recoveryMethodStateSchema>;

export const verifyAuthorityStateSchema = z.object({
  sessionToken: z.string().min(1),
  nextStep: z.literal("verify_authority"),
});
export type VerifyAuthorityState = z.infer<typeof verifyAuthorityStateSchema>;

export const resetPasswordStateSchema = z.object({
  resetToken: z.string().min(1), // Identity Proof
  verificationToken: z.string().min(1), // Authority Proof
});
export type ResetPasswordState = z.infer<typeof resetPasswordStateSchema>;
